import { useState, useCallback, useMemo, useEffect } from 'react'
import { Volume2, VolumeX } from 'lucide-react'

type Props = {
  className?: string
  showLabel?: boolean
}

const BRAND_SENTENCE_EN = 'Thinklyze.'

const PREFERRED_VOICE_NAMES = [
  'Google UK English Male',
  'Google US English',
  'Daniel',
  'Oliver',
  'Alex',
  'Fred',
  'Siri Voice 4',
  'Siri Voice 3'
]

const BrandPronunciationButton = ({ className, showLabel = false }: Props) => {
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null)
  const supportSpeech = useMemo(() => typeof window !== 'undefined' && 'speechSynthesis' in window, [])

  const selectVoice = useCallback(() => {
    if (!supportSpeech) return
    const synth = window.speechSynthesis
    const availableVoices = synth.getVoices()
    if (!availableVoices.length) return

    let selected: SpeechSynthesisVoice | undefined

    for (const name of PREFERRED_VOICE_NAMES) {
      selected = availableVoices.find((v) => v.name.toLowerCase().includes(name.toLowerCase()))
      if (selected) break
    }

    if (!selected) {
      const englishVoices = availableVoices.filter((v) => v.lang && v.lang.toLowerCase().startsWith('en'))
      selected = englishVoices.find((v) => /male|daniel|alex|fred|oliver/.test(v.name.toLowerCase())) ?? englishVoices[0]
    }

    setVoice(selected ?? availableVoices[0] ?? null)
  }, [supportSpeech])

  useEffect(() => {
    if (!supportSpeech) return

    selectVoice()

    const synth = window.speechSynthesis
    const handleVoicesChanged = () => {
      selectVoice()
    }

    synth.addEventListener('voiceschanged', handleVoicesChanged)
    return () => {
      synth.removeEventListener('voiceschanged', handleVoicesChanged)
    }
  }, [supportSpeech, selectVoice])

  const stopSpeaking = useCallback(() => {
    if (!supportSpeech) return
    const synth = window.speechSynthesis
    synth.cancel()
    setIsSpeaking(false)
  }, [supportSpeech])

  const handleSpeak = useCallback(() => {
    if (!supportSpeech) return

    const synth = window.speechSynthesis

    if (synth.speaking) {
      stopSpeaking()
      return
    }

    synth.cancel()

    const utterance = new SpeechSynthesisUtterance(BRAND_SENTENCE_EN)
    utterance.lang = (voice && voice.lang) || 'en-US'
    if (voice) {
      utterance.voice = voice
    }
    utterance.rate = 0.95
    utterance.pitch = 1
    utterance.onend = () => {
      setIsSpeaking(false)
    }

    setIsSpeaking(true)
    synth.speak(utterance)
  }, [supportSpeech, stopSpeaking, voice])

  if (!supportSpeech) {
    return null
  }

  return (
    <button
      type="button"
      onClick={handleSpeak}
      className={`flex items-center gap-2 rounded-full border border-gray-200 bg-white/90 px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-white hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${className ?? ''}`}
      aria-label="Thinklyze nasıl telaffuz edilir?"
      title="Thinklyze nasıl telaffuz edilir?"
    >
      {isSpeaking ? (
        <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {showLabel && 'Thinklyze nasıl okunur?'}
    </button>
  )
}

export default BrandPronunciationButton



